/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { styled } from "@mui/material/styles";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { Icon } from "@iconify/react";
import Dialog from "@mui/material/Dialog";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  Center,
  HStack,
  isAdminViewer,
  isEsopViewer,
  VStack,
} from "../../components/utils";
import { AddOrEditGrant } from "../esopOverview/AddOrEditGrant";
import { Action, CTADropdown } from "../../components/shared/Dropdown";

import { Employee } from "../../types/Employee";
import { InitiateSepartion } from "../../modals/InitiateSepartion";
import AlertDialog from "../../components/shared/AlertDialog";

import {
  useDeleteEmployee,
  useEmployeePageDetails,
  useEmployeeResignationDetails,
  useEmployeeTransactionDetails,
  useEmployees,
  useEnableAccess,
  useGenerateCredentials,
  useRevokeAccess,
  useSettlementDetails,
} from "../../queries/employees";
import { useError } from "../../store/errorStore";
import {
  EmployeePageDetails,
  PauseVestingDto,
} from "../../types/VestingTemplate";
import StatusIndicator from "../../components/shared/StatusIndicator";
import { useAuthorizationStore } from "../../store/useAuthorizationStore";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import useIsMobile from "../../utils/detectDevice";
import { generateJwtToken, generateJwtTokenForEmployee } from "../../api/Esop";
import { JwtPayload } from "../../types/Login";
import { useAuthStore } from "../../store";
import { usePauseVesting } from "../../queries/vest";
import { SettlementLetter } from "../../modals/SettlementLetter";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import { SendSettlementLetter } from "../../modals/SendSettlementLetter";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 5,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === "light" ? "#66D497" : "#F5F5F5",
  },
}));

type EmployeeHeaderTypes = {
  activeTab: string;
} & React.HTMLAttributes<HTMLElement>;

const EmployeeHeader = (props: EmployeeHeaderTypes) => {
  const currency = getCurrencyType();
  const { id } = useParams();
  const _id = id || "";
  const navigate = useNavigate();

  const errorMessage = useError();
  const { data: _employeeResignationDetails } = useEmployeeResignationDetails(
    parseInt(_id, 10)
  );
  const {
    data: _employeePageDetail,
    refetch,
    isLoading,
    isFetched,
  } = useEmployeePageDetails(parseInt(_id, 10));
  const { data: settlementDetails } = useSettlementDetails(parseInt(_id, 10));
  const { data: transactionDetails } = useEmployeeTransactionDetails();
  const transactionDetailsForEmployee = transactionDetails?.data.filter(
    (transaction) => parseInt(transaction.employeeId, 10) === parseInt(_id, 10)
  );
  const isLetterAvailable = !!settlementDetails?.s3Url;
  const isLetterSigned = settlementDetails?.isSigned;
  const isSentForSignature = !!settlementDetails?.isSentForSign;
  const employeePageDetail = _employeePageDetail as EmployeePageDetails;
  useEffect(() => {
    refetch();
  }, []);
  const [dialog, setDialog] = useState<{
    open: boolean;
    user?: Employee | EmployeePageDetails;
    mode?: "Edit" | "Add" | "Clone" | "Delete";
    message?: string;
    primaryAction?: () => void;
  }>({ open: false, user: undefined, mode: "Add" });
  const [employeeDetailsDialog, setEmployeeDetailsDialog] = useState<{
    open: boolean;
    url: string;
  }>({ open: false, url: "" });
  const { activeTab, ...rest } = props;

  const { mutate: deleteEmp } = useDeleteEmployee();
  const { mutate: empRevokeAccess } = useRevokeAccess();
  const { mutate: empEnableAccess } = useEnableAccess();
  const { mutate: pauseVesting } = usePauseVesting();
  const { authority } = useAuthorizationStore();
  const { user } = useAuthStore();
  const [open, setOpen] = useState<boolean>(false);
  const [initiationOpen, setInitiationOpen] = useState<boolean>(false);
  const [settlementLetterOpen, setSettlementLetterOpen] =
    useState<boolean>(false);
  const [isSendSettlementDialogOpen, setIsSendSettlementDialogOpen] =
    useState<boolean>(false);
  const [isSettlementLetterShown, setIsSettlementLetterShown] = useState(false);
  const isUserAdminViewer = isAdminViewer();
  const isUserEsopViewer = isEsopViewer();
  const employeeGrantedOption = employeePageDetail?.grantedOptions || 0;
  const employeeVestedOption = employeePageDetail?.vestedOptions || 0;
  const employeeExercisedOption = employeePageDetail?.exercisedOptions || 0;
  const employeeSharesReceived = employeePageDetail?.sharesReceived || 0;
  const employeeSoldShares = _.sumBy(
    transactionDetailsForEmployee,
    (t) => t.noOfSoldStock || 0
  );
  const amountReceivedForShares = _.sumBy(
    transactionDetailsForEmployee,
    (t) => t.amountReceived || 0
  );
  const userHasLimitedAccess = useMemo(
    () => employeePageDetail?.userHasLimitedAccessToEmployee,
    [employeePageDetail]
  );
  const vestedFactor = employeeVestedOption / employeeGrantedOption;
  const tabs = ["Grant Details", "Profile"];
  const moreActions = [
    {
      name: employeePageDetail?.isPausedVesting
        ? "Resume Vesting"
        : "Pause Vesting",
      disabled:
        !(authority === "ROLE_ADMIN" || authority === "ROLE_ESOP_ADMIN") ||
        userHasLimitedAccess,
    },
    {
      name: "Initiate Separation",
      disabled:
        employeePageDetail?.status !== "Employed" ||
        isUserAdminViewer ||
        isUserEsopViewer ||
        (isLetterAvailable ? !isLetterSigned || !isSentForSignature : false) ||
        userHasLimitedAccess,
    },
    {
      name: "Generate Settlement Letter",
      disabled:
        !(authority === "ROLE_ADMIN" || authority === "ROLE_ESOP_ADMIN") ||
        isLetterSigned ||
        isSentForSignature ||
        userHasLimitedAccess,
    },
    {
      name: `${
        employeePageDetail?.deactivate !== true
          ? "Revoke Access"
          : "Enable Access"
      }`,
      disabled:
        isUserAdminViewer ||
        isUserEsopViewer ||
        userHasLimitedAccess,
    },
    {
      name: "View Full and Final Settlement Letter",
      disabled:
        !(authority === "ROLE_ADMIN" || authority === "ROLE_ESOP_ADMIN") ||
        !isLetterAvailable ||
        userHasLimitedAccess,
    },
    {
      name: "Send Full and Final Settlement Letter",
      disabled:
        !(authority === "ROLE_ADMIN" || authority === "ROLE_ESOP_ADMIN") ||
        !isLetterAvailable ||
        isLetterSigned ||
        isSentForSignature ||
        userHasLimitedAccess,
    },
    {
      name: "Preview Employee View",
      disabled:
        !(authority === "ROLE_ADMIN" || authority === "ROLE_ESOP_ADMIN") ||
        userHasLimitedAccess,
    },
    {
      name: "Delete Employee",
      disabled:
        userHasLimitedAccess ||
        employeePageDetail?.grantedOptions !== 0 ||
        (employeePageDetail?.isLoginProvided !== null
          ? employeePageDetail.isLoginProvided
          : false) ||
        !(authority === "ROLE_ADMIN" || authority === "ROLE_ESOP_ADMIN"),
    },
  ];

  const { mutate: generateCredentials } = useGenerateCredentials();
  function genCred() {
    generateCredentials([parseInt(_id, 10)], {
      onSuccess: () => {
        toast("Credentials Generated successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  }
  function deleteEmployee() {
    if (employeePageDetail?.grantedOptions === 0) {
      deleteEmp(parseInt(_id, 10), {
        onSuccess: () => {
          toast("Employee deleted successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setDialog({ open: false });
          navigate("/options/allEmployees");
        },
        onError: (err: any) => {
          errorMessage.setMessage(err.response.data.reason);
          toast(err.response.data.reason, { type: "error", autoClose: 2000 });
          setDialog({ open: false });
        },
      });
    } else {
      toast("Employee can not be deleted as Employee has grants", {
        type: "error",
        autoClose: 5000,
      });
      setDialog({ open: false });
    }
  }
  function revokeAccess() {
    empRevokeAccess(parseInt(_id, 10), {
      onSuccess: () => {
        toast("Employee is successfully disabled", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  }
  function pauseVestingFunction() {
    const pauseVestingDto: PauseVestingDto = {
      employeeId: employeePageDetail.id,
      isPausedVesting: !employeePageDetail.isPausedVesting,
    };
    pauseVesting(pauseVestingDto, {
      onSuccess: (res) => {
        toast(res, {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response);
        toast(err.response, { type: "error", autoClose: 2000 });
      },
    });
  }

  function enableAccess() {
    empEnableAccess(parseInt(_id, 10), {
      onSuccess: () => {
        toast("Employee is successfully enabled", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      },
    });
  }
  function redirectToEmployeePortal() {
    const employeeJwtPayload: JwtPayload = {
      companyId: user?.company.id as number,
      emailId: employeePageDetail.emailAddress,
      fullName: employeePageDetail.employeeName,
      employeeId: parseInt(_id, 10),
      isViewOnly: true,
      role: "STANDARD",
      type: "EMPLOYEE",
    };
    generateJwtTokenForEmployee(parseInt(_id, 10)).then(
      (jwtToken: { jwtToken: string }) => {
        const jwtAccessToken = jwtToken.jwtToken;
        const employeeUrl = `${process.env.REACT_APP_EMP_PORTAL}/login?token=${jwtAccessToken}&adminEmployeeView=true`;
        setEmployeeDetailsDialog({ open: true, url: employeeUrl });
      },
      (err) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
      }
    );
  }
  function handleAction(user: EmployeePageDetails, action: Action) {
    if (action.disabled) return;
    switch (action.name) {
      case "Initiate Separation": {
        setInitiationOpen(!initiationOpen);
        break;
      }
      case "Generate Settlement Letter": {
        setSettlementLetterOpen(!settlementLetterOpen);
        break;
      }
      case "View Full and Final Settlement Letter": {
        setIsSettlementLetterShown(true);
        break;
      }
      case "Send Full and Final Settlement Letter": {
        toast("Sign before sending it to Employee ", {
          type: "info",
          autoClose: 2000,
        });
        setIsSendSettlementDialogOpen(!isSendSettlementDialogOpen);
        break;
      }
      case "Pause Vesting": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Pause Vesting ?",
          primaryAction: pauseVestingFunction,
        });
        break;
      }
      case "Resume Vesting": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Resume Vesting ?",
          primaryAction: pauseVestingFunction,
        });
        break;
      }
      case "Revoke Access": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Revoke Access ?",
          primaryAction: revokeAccess,
        });
        break;
      }
      case "Enable Access": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Enable Access ?",
          primaryAction: enableAccess,
        });
        break;
      }
      case "Access via Personal Email": {
        setDialog({
          open: true,
          user,
          message: "Provide Access Via Personal Email ID!",
        });
        break;
      }
      case "Send Summary in Email": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to Send Summary in Email ? ",
        });
        break;
      }
      case "Delete Employee": {
        setDialog({
          open: true,
          user,
          message: "Are you sure you want to delete the Employee ?",
          mode: "Delete",
          primaryAction: deleteEmployee,
        });
        break;
      }
      case "Preview Employee View": {
        redirectToEmployeePortal();
        break;
      }
      default: {
        break;
      }
    }
  }
  const [show, setShow] = useState(false);
  const { isMobile } = useIsMobile();
  return (
    <Box
      className={`${isLoading ? "loading" : ""} p-6 pb-0 bg-white rounded-lg"`}
    >
      <Box
        className={`flex ${isMobile ? "flex-col" : "flex-row"}  w-full gap-2`}
      >
        <VStack className="items-center">
          <Box className="bg-slate-501 items-center rounded">
            <Center className=" relative w-40 h-40 bg-gray-100">
              {!employeePageDetail?.imageURL ? (
                <>
                  <Center className=" flex w-40 h-40 rounded-full">
                    <p className=" text-base font-bold">
                      {employeePageDetail?.employeeName.charAt(0)}
                    </p>
                  </Center>
                  <StatusIndicator
                    className="absolute"
                    status={employeePageDetail?.status || ""}
                  />
                </>
              ) : (
                <>
                  {" "}
                  <Center>
                    <img
                      className="block w-40 h-40 space-y-3 bg-gray-400 border-2 rounded-lg"
                      src={employeePageDetail?.imageURL}
                    />
                  </Center>
                  <StatusIndicator
                    className="absolute"
                    status={employeePageDetail?.status}
                  />
                </>
              )}
            </Center>
          </Box>
          <HStack className="flex items-center justify-center mt-2">
            <StatusIndicator
              className=""
              status={employeePageDetail?.status || ""}
            />
            <Box className="text-sm">
              <p>
                {employeePageDetail?.status !== "Employed" &&
                employeePageDetail?.status !== "Resigned"
                  ? "Terminated"
                  : employeePageDetail?.status}
              </p>
            </Box>
          </HStack>
        </VStack>
        <Box className="w-full">
          <HStack className="flex-wrap justify-between gap-4">
            <VStack className="justify-between gap-4">
              <VStack>
                <Box className="flex flex-row items-center space-x-2">
                  <Box className="flex flex-row items-center min-w-min max-w-[450px] font-semibold text-lg text-gray-600">
                    <p>{employeePageDetail?.employeeName}</p>
                    <p className="ml-2">{employeePageDetail?.employeeId}</p>
                  </Box>
                  {employeePageDetail?.isLoginProvided === true ? (
                    <Box className="text-xxs1 bg-green-301 text-green-401 p-1 rounded">
                      <p> Credentials Generated </p>
                    </Box>
                  ) : (
                    <Box className="text-xxs1 p-1 text-red-400 bg-red-100 rounded">
                      <p>Credentials not generated </p>
                    </Box>
                  )}
                </Box>
                <Box className="flex flex-row space-x-4">
                  <Box className="text-gray-201 flex flex-row items-center space-x-1 text-xs">
                    <Box className=" text-xxs1">
                      <Icon icon="carbon:user-filled" />
                    </Box>
                    <Box className="text-xxs1 justify-bottom mr-1">
                      <p>{employeePageDetail?.employmentType}</p>
                    </Box>
                  </Box>
                  <Box className="text-gray-201 flex flex-row items-center space-x-1 text-xs">
                    <Box className="text-xxs1">
                      <Icon icon="icon-park-solid:handbag" />
                    </Box>
                    <Box className="text-xxs1 mr-1">
                      <p>{employeePageDetail?.designation}</p>
                    </Box>
                  </Box>
                  <Box className="text-gray-201 flex flex-row items-center space-x-1 text-xs">
                    <Box className=" text-xxs1">
                      <Icon icon="ci:location" />
                    </Box>
                    <Box className="text-xxs1 mr-1">
                      <p>{employeePageDetail?.residentStatus}</p>
                    </Box>
                  </Box>
                </Box>
              </VStack>
              <VStack className="gap-4">
                <Box className="flex flex-row space-x-4">
                  <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                    <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                      <p>{(employeeGrantedOption || 0).toLocaleString()}</p>
                    </Box>
                    <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                      Granted
                    </Box>
                  </VStack>
                  <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                    <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                      <p>{(employeeVestedOption || 0).toLocaleString()}</p>
                    </Box>
                    <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                      Vested
                    </Box>
                  </VStack>
                  <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                    <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                      <p>{(employeeExercisedOption || 0).toLocaleString()}</p>
                    </Box>
                    <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                      Exercised
                    </Box>
                  </VStack>
                </Box>
                <Box className="flex flex-row space-x-4">
                  <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                    <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                      <p>{(employeeSharesReceived || 0).toLocaleString()}</p>
                    </Box>
                    <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                      Shares received
                    </Box>
                  </VStack>
                  <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                    <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                      <p>
                        {employeePageDetail?.userHasLimitedAccessToEmployee
                          ? 0
                          : (employeeSoldShares || 0).toLocaleString()}
                      </p>
                    </Box>
                    <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                      Shares sold
                    </Box>
                  </VStack>
                  <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                    <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                      <p>
                        {employeePageDetail?.userHasLimitedAccessToEmployee
                          ? employeeSharesReceived.toLocaleString()
                          : (
                              employeeSharesReceived - employeeSoldShares || 0
                            ).toLocaleString()}
                      </p>
                    </Box>
                    <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                      Balance shares
                    </Box>
                  </VStack>
                </Box>
                {employeePageDetail?.showPPSToEmployees ? (
                  <Box className=" flex flex-col">
                    <Box className="flex flex-row space-x-4">
                      <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                        <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                          <p>
                            {formatCurrency(
                              employeePageDetail?.ppsforEmployee || 0,
                              currency
                            )}
                          </p>
                        </Box>

                        <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                          Price Per Share
                        </Box>
                      </VStack>
                      <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                        <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                          <p>
                            {formatCurrency(
                              (employeePageDetail?.totalOptionValue || 0) -
                                amountReceivedForShares,
                              currency
                            )}
                          </p>
                        </Box>
                        <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                          Total Option Value
                        </Box>
                      </VStack>
                      <VStack className="xl:w-36 lg:w-28 md:w-20 sm:w-16 p-2 text-xs border border-dashed rounded">
                        <Box className="text-black-dark text-xs3 p-1 pb-0 font-semibold">
                          <p>
                            {formatCurrency(
                              employeePageDetail?.totalOptionCost,
                              currency
                            )}
                          </p>
                        </Box>
                        <Box className="text-xxs1 text-gray-201 p-1 mr-2">
                          Total Option Cost
                        </Box>
                      </VStack>
                    </Box>
                  </Box>
                ) : (
                  <p></p>
                )}
              </VStack>
            </VStack>
            <VStack className="justify-between gap-4">
              {/* vested factor */}
              <Box className=" flex flex-row space-x-4">
                <ButtonPrimary
                  className={``}
                  disabled={isUserAdminViewer || isUserEsopViewer}
                  onClick={() => {
                    setDialog({
                      open: true,
                      message: `Are you sure you want to generate credentials`,
                      primaryAction: genCred,
                    });
                  }}
                >
                  {employeePageDetail?.isLoginProvided !== true
                    ? "Generate Credentials"
                    : "Re-Generate Credentials"}
                </ButtonPrimary>
                <Box className="items-center px-2 py-3 bg-gray-300 rounded-sm">
                  <CTADropdown
                    actions={moreActions}
                    onAction={(action) =>
                      handleAction(employeePageDetail, action)
                    }
                  />
                </Box>

                <Dialog open={open} maxWidth="md">
                  <AddOrEditGrant
                    onClose={() => setOpen(!open)}
                    grant={undefined}
                    mode={dialog.mode}
                  />
                </Dialog>

                {isLetterAvailable && (
                  <Dialog
                    open={isSettlementLetterShown}
                    onClose={() => setIsSettlementLetterShown(false)}
                    maxWidth="lg"
                  >
                    <LazyPdfDocument url={settlementDetails.s3Url} />
                  </Dialog>
                )}
                <Dialog open={initiationOpen} maxWidth="md">
                  <InitiateSepartion
                    onClose={() => setInitiationOpen(!initiationOpen)}
                  />
                </Dialog>
                <Dialog open={settlementLetterOpen} maxWidth="md">
                  <SettlementLetter
                    onClose={() =>
                      setSettlementLetterOpen(!settlementLetterOpen)
                    }
                  />
                </Dialog>
                <Dialog open={isSendSettlementDialogOpen} maxWidth="md">
                  <SendSettlementLetter
                    onClose={() =>
                      setIsSendSettlementDialogOpen(!isSendSettlementDialogOpen)
                    }
                  />
                </Dialog>
                <Dialog
                  open={dialog.open}
                  onClose={() => setDialog({ open: false })}
                  maxWidth="md"
                >
                  <AlertDialog
                    onClose={() => setDialog({ open: false })}
                    open={dialog.open}
                    message={dialog.message}
                    onPrimaryAction={dialog.primaryAction}
                    onSecondaryAction={() => setDialog({ open: false })}
                  />
                </Dialog>
                <Dialog
                  className="overflow-x-auto overflow-y-auto"
                  open={employeeDetailsDialog.open}
                  maxWidth="xl"
                >
                  <Box className="overflow-x-auto overflow-y-auto">
                    <Box className="py-7 sticky px-10 text-lg font-medium border-b">
                      <h6 className="flex justify-between">
                        {"Employee Details "}
                        <span
                          onClick={() =>
                            setEmployeeDetailsDialog({ open: false, url: "" })
                          }
                          className="cursor-pointer"
                        >
                          X
                        </span>
                      </h6>
                    </Box>
                    <iframe
                      className="min-h-[1080px] min-w-[1440px]"
                      src={employeeDetailsDialog.url}
                    ></iframe>
                  </Box>
                </Dialog>
              </Box>
              {(employeeGrantedOption || 0) > 0 ? (
                <Box>
                  <VStack className="min-w-min p-2">
                    <Box className=" flex flex-row justify-between">
                      <p className="text-stone-300 text-xs4 ">Vested factor</p>
                      <p className="text-xs4 text-black">
                        {(vestedFactor * 100).toFixed()}
                        {" %"}
                      </p>
                    </Box>
                    <BorderLinearProgress
                      className=""
                      variant="determinate"
                      value={vestedFactor * 100}
                    />
                  </VStack>
                </Box>
              ) : (
                ""
              )}
            </VStack>
          </HStack>
        </Box>
      </Box>
      <HStack className="flex-center gap-8 mb-0">
        {tabs.map((item, i) => (
          <Box
            key={i}
            className={`text-xs3 font-semibold cursor-pointer py-4 ${
              item === activeTab
                ? "border-b-2 border-solid border-red-500 text-red-500"
                : "text-gray-201 "
            }`}
            {...rest}
          >
            {item}
          </Box>
        ))}
      </HStack>
    </Box>
  );
};

export default EmployeeHeader;
